import React, { Fragment } from 'react';
import ReactDom from 'react-dom';
import HelloSign from 'hellosign-embedded';
import dayjs from 'dayjs';
import styled from 'styled-components';
import {
  Flex, Text, Box, Checkbox, Link, ActionNote, StateTaxFormInfo,
} from '../components';
import * as S from '../styles';
import FormConsumer from '../collect-info/FormConsumers';
import { Button, SecondaryButton } from '../styles';
import Axios from '../request';
import { toFormData } from '../../../assets/js/utils';
import {
  skipDomainVerificationHelloSign,
  helloSignClientId,
} from '../../../config';
import { STEPS, step1Questions, formLink } from './data';
import StateTaxFormLayout from '../state-tax-withholding/common/state-tax-content-layout';
import { PageTitle } from '../biz-components/page-title';

const getSecurePDFLink = (digestKey) => `${APIHOST}/v1/w4_form_answers/:digest_key/pdf`.replace(
  ':digest_key',
  digestKey,
);

function BlueNote({ children }) {
  return (
    <Flex
      bg="#EEF5FF"
      borderRadius="4px"
      py={20}
      pl={16}
      pr={24}
      alignItems="flex-start"
    >
      <Box mt="2px">
        <img src="/j/images/employee-onboarding/blue-note.svg" />
      </Box>
      <Flex flex={1} ml={10} fontSize="12px" color="#1E1E1E">
        {children}
      </Flex>
    </Flex>
  );
}

const StyledStateTaxFormInfo = styled(StateTaxFormInfo)`
  margin-bottom: 0px
`;

class EmployeeFillForm extends React.Component {
  constructor(props) {
    super(props);
    const {
      prefilledData = {},
      dataFromEi = {},
      action: {
        w4_form_answer: { data = {}, version },
      },
    } = props;
    const step1QuestionsWithAnswers = step1Questions.map((question) => {
      const value = data[question.id] || prefilledData[question.id] || dataFromEi[question.id] || '';
      if (!data[question.id] && (prefilledData[question.id] || dataFromEi[question.id])) {
        question.locked = true;
      }
      const answer = {
        answer: {
          [question.id]: value,
        },
      };
      return {
        ...question,
        answer,
        version,
      };
    });
    const isExemptFromWithholding = (() => {
      if (data.is_exempt_from_withholding === 'true') return true;
      if (data.is_exempt_from_withholding === 'false') return false;
      return false;
    })();

    const willUserTakingStep2 = (() => {
      if (data.two_job === 'true') return true;
      if (data.two_job === 'false') return true;
      return undefined;
    })();
    let currentStep = STEPS.step0;
    if (props.viewResultAsManager) {
      currentStep = STEPS.step5;
    }

    const currentVersion = parseInt(version, 10);
    const currentYear = Number.isNaN(currentVersion)
      ? dayjs().year()
      : currentVersion;
    const previousYear = currentYear - 1;

    this.state = {
      currentStep,
      saveDataError: false,
      signError: false,
      currentYear,
      previousYear,
      version,

      // step check exemption status data, default no excemption.
      isExemptFromWithholding,

      // step 1 data ( Enter personal information )
      step1Questions: step1QuestionsWithAnswers,

      // step 2 data
      willUserTakingStep2, // undefined, true and false
      selectOptionC: data.two_job === 'true',

      // step 3 data
      willUserTakingStep3:
        ((data.qualifying_children_num || data.qualifying_other_num) && true)
        || undefined,
      qualifyingChildrenNum: data.qualifying_children_num || '',
      qualifyingOtherNum: data.qualifying_other_num || '',

      // step 4 data
      otherIncome: data.other_income || '',
      deductions: data.deductions || '',
      extraWithholding: data.extra_withholding || '',

      // step 6 data
      signLink: undefined,
    };
    this.step1Form = React.createRef();
    this.step2Form = React.createRef();
    this.step3Form = React.createRef();
    this.el = document.createElement('div');

    this.isOnboardingV2 = isOnboardingV2 === 'true';
  }

  componentDidMount() {
    const root = document.getElementById('js-note-container');
    root.appendChild(this.el);
  }

  componentWillUnmount() {
    this.hellosignClient && this.hellosignClient.close();
    const root = document.getElementById('js-note-container');
    root.removeChild(this.el);
  }

  initHelloSign() {
    const { signLink } = this.state;
    const containerId = 'iframe-container';

    this.hellosignClient = new HelloSign({
      clientId: helloSignClientId,
    });

    this.hellosignClient.once(HelloSign.events.SIGN, (eventData) => {
      const { event } = eventData;
      if (event === HelloSign.EVENT_SIGNED) {
        // Call api to mark this document as signed
        const { digest_key: digestKey } = this.props.action.w4_form_answer;
        const path = `${APIHOST}/v1/w4_form_answers/:digest_key/signed`.replace(
          ':digest_key',
          digestKey,
        );
        Axios.post(path, { sign_url: signLink })
          .then(() => {
            this.props.onSignedSuccess();
          })
          .catch((error) => {
            let signError = 'We were unable to process your signature. Please try again.';
            if (error && error.response && error.response.data) {
              const { error_code: errorCode, message } = error.response.data;
              if (errorCode === 60033) {
                signError = message;
              }
            }
            this.setState({ signError });
          });
      }
    });

    const options = {
      skipDomainVerification: skipDomainVerificationHelloSign,
      container: document.getElementById(containerId),
    };

    this.hellosignClient.open(signLink, options);
  }

  renderStep1Form(isReviewStep) {
    let questions;
    if (this.state.isExemptFromWithholding) {
      questions = this.state.step1Questions.filter(
        (item) => item.id !== 'federal_filing_status',
      );
    } else {
      questions = this.state.step1Questions;
    }

    return (
      <FormConsumer
        questions={questions}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = this.state.step1Questions.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.anser,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          this.setState({ step1Questions: questions });
        }}
        ref={this.step1Form}
      />
    );
  }

  saveFormData(data, successCallback) {
    const { digest_key: digestKey } = this.props.action.w4_form_answer;
    this.setState({ isSubmitting: true, saveDataError: false });
    const path = `${APIHOST}/v1/w4_form_answers/${digestKey}`;
    Axios.put(path, toFormData({ data }))
      .then((responseData) => {
        this.setState({ isSubmitting: false });
        successCallback(responseData);
      })
      .catch((error) => {
        let saveDataError = 'Something went wrong while saving data.';
        if (error && error.response && error.response.data) {
          const { error_code: errorCode, message } = error.response.data;
          if (errorCode === 60032) {
            saveDataError = message;
          }
        }
        this.setState({ isSubmitting: false, saveDataError });
      });
  }

  renderStep0Button() {
    let disabled;
    let disabledText;
    if (this.props.previewMode) {
      disabled = true;
      disabledText = 'You cannot complete this step in Preview Mode.';
    } else if (this.props.companyStaff && this.props.companyStaff.id) {
      disabled = true;
      disabledText = 'This step should be completed by Employee.';
    }

    return (
      <Flex flexDirection="column">
        <Button
          disabled={disabled}
          onClick={() => {
            this.setState({ currentStep: STEPS.exempt_status });
          }}
        >
          Start
        </Button>
        <Flex justifyContent="flex-end">
          {disabled && <ActionNote text={disabledText} />}
        </Flex>
      </Flex>
    );
  }

  renderStep0() {
    const { version } = this.state;
    const faqLink = 'https://www.irs.gov/newsroom/faqs-on-the-2020-form-w-4';
    if (version === '2021') {
      // Do not need to update the link, because the official link is not updated for 2021.
      // faqLink = 'https://www.irs.gov/newsroom/faqs-on-the-2020-form-w-4';
    }
    return (
      <Flex column>
        {/* <Text fontSize={11} semibold color='#0D8CE6'>
          Welcome
        </Text> */}
        {
          this.isOnboardingV2 ? null : (
            <Text fontSize="22px" bold>
              Form W-4
            </Text>
          )
        }
        <Text fontSize="12px" lineHeight="16px" mt={20}>
          You will electronically complete the Form W-4, so that your employer
          can withhold the correct federal income tax from your pay.
        </Text>
        <Text mt={37} fontSize="14px" semibold>
          Before you get started, familiarize yourself with the Form W-4 with
          the following resources:
        </Text>
        <Box mt={16}>
          <Link href={formLink} target="_blank">
            Form W-4 and instructions
          </Link>
        </Box>
        <Box mt={16}>
          <Link href={faqLink} target="_blank">
            Frequently asked questions
          </Link>
        </Box>
        <Box mt={16}>
          <Link
            href="https://www.irs.gov/individuals/tax-withholding-estimator"
            target="_blank"
          >
            IRS Withholding calculator
          </Link>
        </Box>
        <Box mt={60} />
        {this.renderStep0Button()}
      </Flex>
    );
  }

  renderFixedModuleHeader(text, link) {
    return (
      <a
        target="_blank"
        rel="noopener noreferrer"
        style={{ lineHeight: '20px', textDecoration: 'none', color: '#3F51B5' }}
        href={link}
      >
        <Box width="fit-content" marginBottom="8px">
          <Flex alignItems="center" backgroundColor="#F4F4F5" padding="6px 8px">
            <Text fontSize={12} color="#787883" fontWeight="600">
              {text}
            </Text>
            <img
              src="/j/images/external-link-black.svg"
              style={{ marginLeft: 6 }}
              width={12}
              height={12}
              alt="external link"
              color="black"
            />
          </Flex>
        </Box>
      </a>
    );
  }

  renderExemptionStatusStep() {
    const { currentYear } = this.state;
    const { previousYear } = this.state;

    return (
      <Flex column>
        {this.renderFixedModuleHeader(
          'Form W-4: Federal tax withholding',
          formLink,
        )}
        <Text fontSize="22px" bold>
          Exemption from withholding
        </Text>

        <Text fontSize="12px" lineHeight="16px" mt={20}>
          You may claim exemption from withholding for
          {' '}
          {currentYear}
          {' '}
          if you meet
          both of the following conditions:
          <Box mt={20} ml={20}>
            <ul>
              <li>
                You had no federal income tax liability in
                {' '}
                {previousYear}
              </li>
              <li>
                You expect to have no federal income tax liability in
                {' '}
                {currentYear}
              </li>
            </ul>
          </Box>
        </Text>

        <Text mt={37} mb={10} fontSize="14px" semibold>
          Would you like to claim exemption from withholding in
          {' '}
          {previousYear}
          ?
        </Text>

        <label>
          <Flex alignItems="center">
            <S.Input
              type="radio"
              value={!this.state.isExemptFromWithholding}
              checked={!this.state.isExemptFromWithholding}
              width="auto"
              onChange={(e) => {
                this.setState({
                  isExemptFromWithholding: !this.state.isExemptFromWithholding,
                });
              }}
            />
            <Box ml={2} />
            <Text fontSize="12px" lineHeight="16px">
              No, I am not exempt from withholding.
            </Text>
          </Flex>
        </label>
        <label>
          <Flex alignItems="center">
            <S.Input
              type="radio"
              value={this.state.isExemptFromWithholding}
              checked={this.state.isExemptFromWithholding}
              width="auto"
              onChange={(e) => {
                this.setState({
                  isExemptFromWithholding: !this.state.isExemptFromWithholding,
                });
              }}
            />
            <Box ml={2} />
            <Text fontSize="12px" lineHeight="16px">
              Yes, I am exempt from withholding.
            </Text>
          </Flex>
        </label>

        <Flex mt={20}>
          <SecondaryButton
            onClick={() => {
              this.setState({ currentStep: STEPS.step0 });
            }}
          >
            Back
          </SecondaryButton>
          <Flex flex={1} column ml={2}>
            <Button
              onClick={() => {
                const data = {
                  is_exempt_from_withholding:
                    this.state.isExemptFromWithholding,
                };
                this.saveFormData(data, () => {
                  this.setState({
                    currentStep: STEPS.step1,
                  });
                });
              }}
              loading={this.state.isSubmitting}
            >
              Next
            </Button>
          </Flex>
        </Flex>
      </Flex>
    );
  }

  renderExemptionStatus() {
    return (
      <Flex column>
        <Text fontSize="18px" bold>
          Exempt Status
        </Text>
        <Box mt={16} />
        <label>
          <Flex alignItems="center">
            <S.Input
              type="radio"
              checked={!this.state.isExemptFromWithholding}
              width="auto"
              disabled
            />
            <Box ml={2} />
            <Text fontSize="12px" lineHeight="16px">
              No, I am not exempt from withholding.
            </Text>
          </Flex>
        </label>
        <label>
          <Flex alignItems="center">
            <S.Input
              type="radio"
              checked={this.state.isExemptFromWithholding}
              width="auto"
              disabled
            />
            <Box ml={2} />
            <Text fontSize="12px" lineHeight="16px">
              Yes, I am exempt from withholding.
            </Text>
          </Flex>
        </label>
        {this.props.viewResultAsManager
          && this.state.isExemptFromWithholding && (
            <Text fontSize="11px" lineHeight="16px" color="red">
              The employee indicated they are exempt from tax withholding. Only
              the necessary fields for Form W-4 were collected.
            </Text>
        )}
        <Box mt={44} />
      </Flex>
    );
  }

  renderW4Link() {
    return (
      <div style={{ marginTop: 20 }}>
        <Text fontSize="14px">
          The following steps are required so that your employer can withhold
          the correct federal income tax from your pay.
        </Text>
        <a
          target="_blank"
          rel="noopener noreferrer"
          style={{
            lineHeight: '20px',
            textDecoration: 'none',
            color: '#3F51B5',
          }}
          href={formLink}
        >
          <Flex alignItems="center">
            <Text>Learn more about Form W-4 </Text>
            <img
              src="/j/images/external-link.svg"
              style={{ marginLeft: 6 }}
              width={16}
              height={16}
              alt="external link"
            />
          </Flex>
        </a>
      </div>
    );
  }

  renderStep1() {
    const { step1Questions: questions, isSubmitting } = this.state;
    return (
      <Flex column>
        {this.renderFixedModuleHeader(
          'Form W-4: Federal tax withholding',
          formLink,
        )}
        <Text fontSize="22px" bold>
          Enter Personal Information
        </Text>
        <Flex column mt={10}>
          {this.renderStep1Form()}
        </Flex>

        <Flex>
          <SecondaryButton
            onClick={() => {
              this.setState({ currentStep: STEPS.exempt_status });
            }}
          >
            Back
          </SecondaryButton>
          <Flex flex={1} column ml={2}>
            <Button
              onClick={() => {
                if (this.step1Form && this.step1Form.current) {
                  this.step1Form.current.validate((error) => {
                    if (error) return;
                    // No error, proceed to save
                    // - save answers to server
                    // - processed to next step
                    // - if employee is exempt from withholding, skip federal_filing_status
                    // and go directly to preview and sign
                    const data = questions.reduce((acc, question) => {
                      if (
                        this.state.isExemptFromWithholding
                        && question.id === 'federal_filing_status'
                      ) {
                        return { ...acc, federal_filing_status: '' };
                      }
                      return { ...acc, ...question.answer.answer };
                    }, {});
                    this.saveFormData(data, () => {
                      if (this.state.isExemptFromWithholding) {
                        this.setState({ currentStep: STEPS.step5 });
                      } else {
                        this.setState({ currentStep: STEPS.step2 });
                      }
                    });
                  });
                }
              }}
              loading={isSubmitting}
            >
              Next
            </Button>
          </Flex>
        </Flex>
      </Flex>
    );
  }

  renderStep2Form(isReviewStep) {
    return (
      <Flex column mb={50}>
        <Text black>
          Do
          {' '}
          <strong>only one</strong>
          {' '}
          of the following
        </Text>
        <Flex mt={24}>
          <Text bold color="black">
            (a)
          </Text>
          <Flex ml={2}>
            <Text>
              Use the estimator at
              {' '}
              <a href="https://www.irs.gov/W4App">www.irs.gov/W4App</a>
              {' '}
              for most accurate withholding for this step (and Steps 3-4). If you
              or your spouse have self-employment income, use this option; or
            </Text>
          </Flex>
        </Flex>
        <Flex mt={24}>
          <Text bold color="black">
            (b)
          </Text>
          <Flex ml={2}>
            <Text>
              Use the Multiple Jobs Worksheet on
              {' '}
              <a href={formLink} target="_blank" rel="noreferrer">
                page 3
              </a>
              {' '}
              and enter the result in Step 4(c) below; or
            </Text>
          </Flex>
        </Flex>
        <Flex mt={24}>
          <Text bold color="black">
            (c)
          </Text>
          <Flex ml={2} column>
            <Text>
              If there are only two jobs total, you may check this box. Do the
              same on Form W-4 for the other job. This option is generally more
              accurate than (b) if pay at the lower paying job is more than half
              of the pay at the higher paying job. Otherwise, (b) is more
              accurate
            </Text>
            <Flex alignItems="center" mt={20}>
              <Box ml={1} />
              <label>
                <Checkbox
                  id="selectC"
                  checked={this.state.selectOptionC}
                  onChange={(e) => {
                    this.setState({ selectOptionC: e.target.checked });
                  }}
                  disabled={isReviewStep}
                />
                <Text ml={10} display="inline-block">
                  Select option (c)
                </Text>
              </label>
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    );
  }

  renderStep2() {
    const { willUserTakingStep2 } = this.state;
    return (
      <Flex column>
        {this.renderFixedModuleHeader(
          'Form W-4: Federal tax withholding',
          formLink,
        )}
        <Text fontSize="22px" bold>
          Multiple Jobs or Spouse Works
        </Text>

        <Flex column mt={10}>
          <BlueNote>
            <Box>
              <Text fontSize="12px" bold>
                ONLY complete this step if one of the following applies to you:
              </Text>
              <br />
              <Text fontSize="12px">
                (1) You hold more than one job at a time
              </Text>
              <Text fontSize="12px">
                (2) You are married filing jointly and your spouse also works.
              </Text>
              <br />
              <a
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  lineHeight: '20px',
                  textDecoration: 'none',
                  color: '#3F51B5',
                }}
                href={formLink}
              >
                <Text fontSize="12">
                  View Form W-4 page 2 for additional guidance
                </Text>
              </a>
            </Box>
          </BlueNote>
        </Flex>
        <Flex mt={24} mb={40}>
          <Flex alignItems="center" mr={24}>
            <input
              type="radio"
              id="1"
              name="willUserTakingStep2"
              value="false"
              checked={willUserTakingStep2 === false}
              onChange={() => {
                this.setState({
                  willUserTakingStep2: false,
                  selectOptionC: false,
                });
              }}
            />
            <Box ml={1} />
            <label htmlFor="1">Skip this step</label>
          </Flex>
          <Flex alignItems="center">
            <input
              type="radio"
              id="2"
              name="willUserTakingStep2"
              value="true"
              checked={willUserTakingStep2 === true}
              onChange={() => {
                this.setState({
                  willUserTakingStep2: true,
                });
              }}
            />
            <Box ml={1} />
            <label htmlFor="2">Take this step</label>
          </Flex>
        </Flex>
        {willUserTakingStep2 && this.renderStep2Form()}
        <Flex>
          <SecondaryButton
            onClick={() => {
              this.setState({ currentStep: STEPS.step1, saveDataError: false });
            }}
          >
            Back
          </SecondaryButton>
          <Flex flex={1} column ml={2}>
            <Button
              onClick={() => {
                const data = { two_job: this.state.selectOptionC };
                this.saveFormData(data, () => {
                  this.setState({
                    currentStep: STEPS.step3,
                  });
                });
              }}
              loading={this.state.isSubmitting}
              disabled={this.state.willUserTakingStep2 === undefined}
            >
              Next
            </Button>
          </Flex>
        </Flex>
      </Flex>
    );
  }

  renderStep3Form(isReviewStep) {
    const {
      qualifyingChildrenNum,
      qualifyingOtherNum,
      qualifyingOtherNumError,
      qualifyingChildrenNumError,
      version,
    } = this.state;
    const multiplier = version === '2026' ? 2200 : 2000;
    const childrenDeduction = Number(qualifyingChildrenNum) * multiplier;
    const otherDeduction = Number(qualifyingOtherNum) * 500;
    const totalDeduction = childrenDeduction + otherDeduction;
    return (
      <Flex column mb={50}>
        <S.QuestionWrapper mb="24px">
          <Flex>
            <Flex
              justifyContent="space-between"
              alignItems="center"
              width={1}
              mb="7px"
            >
              <S.QuestionLabel isRequired={false}>
                Number of qualifying children under age 17
              </S.QuestionLabel>
            </Flex>
          </Flex>
          <Flex position="relative">
            <Flex>
              <S.Input
                disabled={isReviewStep}
                type="text"
                value={qualifyingChildrenNum}
                width="auto"
                onChange={(e) => {
                  const numberValue = e.target.value.replace(/[^0-9]/g, '');
                  this.setState({
                    qualifyingChildrenNum: numberValue,
                    qualifyingChildrenNumError: undefined,
                  });
                }}
              />
            </Flex>
            <Flex
              ml="8px"
              width="132px"
              px="10px"
              alignItems="center"
              bg="#f7f5f5"
              border="1px solid #dee1ec"
              borderRadius="4px"
            >
              <Text color="black">
                $
                {childrenDeduction}
              </Text>
            </Flex>
          </Flex>
          {qualifyingChildrenNumError && (
            <Text color="red">{qualifyingChildrenNumError}</Text>
          )}
        </S.QuestionWrapper>
        <S.QuestionWrapper mb="24px">
          <Flex>
            <Flex
              justifyContent="space-between"
              alignItems="center"
              width={1}
              mb="7px"
            >
              <S.QuestionLabel isRequired={false}>
                Number of other dependents
              </S.QuestionLabel>
            </Flex>
          </Flex>
          <Flex position="relative">
            <Flex>
              <S.Input
                type="text"
                disabled={isReviewStep}
                value={qualifyingOtherNum}
                width="auto"
                onChange={(e) => {
                  const numberValue = e.target.value.replace(/[^0-9]/g, '');
                  this.setState({
                    qualifyingOtherNum: numberValue,
                    qualifyingOtherNumError: undefined,
                  });
                }}
              />
            </Flex>
            <Flex
              ml="8px"
              width="132px"
              px="10px"
              alignItems="center"
              bg="#f7f5f5"
              border="1px solid #dee1ec"
              borderRadius="4px"
            >
              <Text color="black">
                $
                {otherDeduction}
              </Text>
            </Flex>
          </Flex>
          {qualifyingOtherNumError && (
            <Text color="red">{qualifyingOtherNumError}</Text>
          )}
        </S.QuestionWrapper>
        <S.QuestionWrapper mb="24px">
          <Flex>
            <Flex
              justifyContent="space-between"
              alignItems="center"
              width={1}
              mb="7px"
            >
              <S.QuestionLabel isRequired={false}>Total amount</S.QuestionLabel>
            </Flex>
          </Flex>
          <Flex position="relative" flexDirection="column">
            <Flex
              width="132px"
              px="10px"
              alignItems="center"
              bg="#f7f5f5"
              border="1px solid #dee1ec"
              borderRadius="4px"
              height="33px"
            >
              <Text color="black">
                $
                {totalDeduction}
              </Text>
            </Flex>
          </Flex>
        </S.QuestionWrapper>
      </Flex>
    );
  }

  renderStep3() {
    const { willUserTakingStep3: takeThisStep } = this.state;

    return (
      <Flex column>
        {this.renderFixedModuleHeader(
          'Form W-4: Federal tax withholding',
          formLink,
        )}
        <Text fontSize="22px" bold>
          Claim Dependents
        </Text>

        <Flex column mt={10}>
          <BlueNote>
            <Box>
              <Text fontSize="12px" bold>
                ONLY complete this step if your income will be $200,000 or less
                ($400,000 or less if married filing jointly).
              </Text>
              <Text fontSize="12px">
                Skip this step if you or your spouse already claimed dependents
                when filing a W-4 for another job.
              </Text>
              <br />
              <a
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  lineHeight: '20px',
                  textDecoration: 'none',
                  color: '#3F51B5',
                }}
                href={formLink}
              >
                <Text fontSize="12">
                  View Form W-4 page 2 for additional guidance
                </Text>
              </a>
            </Box>
          </BlueNote>
        </Flex>
        <Flex mt={24} mb={40}>
          <Flex alignItems="center" mr={24}>
            <input
              type="radio"
              id="1"
              name="takeThisStep"
              value="false"
              checked={takeThisStep === false}
              onChange={() => {
                this.setState({
                  willUserTakingStep3: false,
                  qualifyingChildrenNum: '',
                  qualifyingOtherNum: '',
                });
              }}
            />
            <Box ml={1} />
            <label htmlFor="1">Skip this step</label>
          </Flex>
          <Flex alignItems="center">
            <input
              type="radio"
              id="2"
              name="takeThisStep"
              value="true"
              checked={takeThisStep === true}
              onChange={() => {
                this.setState({
                  willUserTakingStep3: true,
                });
              }}
            />
            <Box ml={1} />
            <label htmlFor="2">Take this step</label>
          </Flex>
        </Flex>
        {takeThisStep && this.renderStep3Form()}
        <Flex>
          <SecondaryButton
            onClick={() => {
              this.setState({ currentStep: STEPS.step2, saveDataError: false });
            }}
          >
            Back
          </SecondaryButton>
          <Flex flex={1} column ml={2}>
            <Button
              onClick={() => {
                const { qualifyingChildrenNum, qualifyingOtherNum } = this.state;
                if (qualifyingOtherNum >= 99 || qualifyingChildrenNum >= 99) {
                  this.setState({
                    qualifyingOtherNumError:
                      qualifyingOtherNum >= 99
                      && 'Enter a value that is less than 99.',
                    qualifyingChildrenNumError:
                      qualifyingChildrenNum >= 99
                      && 'Enter a value that is less than 99.',
                  });
                  return;
                }
                const data = {
                  qualifying_children_num: qualifyingChildrenNum,
                  qualifying_other_num: qualifyingOtherNum,
                };
                this.saveFormData(data, () => {
                  this.setState({
                    currentStep: STEPS.step4,
                    qualifyingOtherNumError: undefined,
                    qualifyingChildrenNumError: undefined,
                  });
                });
              }}
              loading={this.state.isSubmitting}
              disabled={takeThisStep === undefined}
            >
              Next
            </Button>
          </Flex>
        </Flex>
      </Flex>
    );
  }

  renderStep4Form(isReviewStep) {
    const { otherIncome, deductions, extraWithholding } = this.state;
    return (
      <Flex column>
        <S.QuestionWrapper mb="24px">
          <Flex>
            <Flex
              justifyContent="space-between"
              alignItems="center"
              width={1}
              mb="7px"
            >
              <S.QuestionLabel isRequired={false}>
                Other income (not from jobs). If you want tax withheld for other
                income you expect this year that won’t have withholding, enter
                the amount of other income here. This may include interest,
                dividends, and retirement income. (4a)
              </S.QuestionLabel>
            </Flex>
          </Flex>
          <Flex position="relative">
            <S.Input
              disabled={isReviewStep}
              type="text"
              width="auto"
              value={`$ ${otherIncome}`}
              onChange={(e) => {
                const value = e.target.value.replace('$ ', '');
                const re = /^\d*(\.)?\d?\d?$/;
                const isPositiveDecimal = re.test(value);
                if (isPositiveDecimal) {
                  this.setState({ otherIncome: value });
                }
              }}
            />
          </Flex>
        </S.QuestionWrapper>
        <S.QuestionWrapper mb="24px">
          <Flex>
            <Flex
              justifyContent="space-between"
              alignItems="center"
              width={1}
              mb="7px"
            >
              <S.QuestionLabel isRequired={false}>
                Deductions. If you expect to claim deductions other than the
                standard deduction and want to reduce your withholding, use the
                Deductions Worksheet on page 3 and enter the result here. (4b)
              </S.QuestionLabel>
            </Flex>
          </Flex>
          <Flex position="relative">
            <S.Input
              disabled={isReviewStep}
              type="text"
              value={`$ ${deductions}`}
              width="auto"
              onChange={(e) => {
                const value = e.target.value.replace('$ ', '');
                const re = /^\d*(\.)?\d?\d?$/;
                const isPositiveDecimal = re.test(value);
                if (isPositiveDecimal) {
                  this.setState({ deductions: value });
                }
              }}
            />
          </Flex>
        </S.QuestionWrapper>
        <S.QuestionWrapper mb="24px">
          <Flex>
            <Flex
              justifyContent="space-between"
              alignItems="center"
              width={1}
              mb="7px"
            >
              <S.QuestionLabel isRequired={false}>
                Extra withholding. Enter any additional tax you want withheld
                each pay period (4c)
              </S.QuestionLabel>
            </Flex>
          </Flex>
          <Flex position="relative">
            <S.Input
              type="text"
              disabled={isReviewStep}
              value={`$ ${extraWithholding}`}
              width="auto"
              onChange={(e) => {
                const value = e.target.value.replace('$ ', '');
                const re = /^\d*(\.)?\d?\d?$/;
                const isPositiveDecimal = re.test(value);
                if (isPositiveDecimal) {
                  this.setState({ extraWithholding: value });
                }
              }}
            />
          </Flex>
        </S.QuestionWrapper>
      </Flex>
    );
  }

  renderStep4() {
    return (
      <Flex column>
        {this.renderFixedModuleHeader(
          'Form W-4: Federal tax withholding',
          formLink,
        )}
        <Text fontSize="22px" bold>
          Other Adjustments (Optional)
        </Text>

        <Flex column mt={10} mb={30}>
          <BlueNote>
            <Box>
              <Text fontSize="12px" bold>
                Only complete this step if it applies to you.
              </Text>
              <Text fontSize="12px">
                Skip this step if you or your spouse already reported other
                adjustments when filing a W-4 for another job.
              </Text>
              <br />
              <a
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  lineHeight: '20px',
                  textDecoration: 'none',
                  color: '#3F51B5',
                }}
                href={formLink}
              >
                <Text fontSize="12">
                  View Form W-4 page 2 for additional guidance
                </Text>
              </a>
            </Box>
          </BlueNote>
        </Flex>
        <Flex column mb={10}>
          {this.renderStep4Form()}
        </Flex>
        <Flex>
          <SecondaryButton
            onClick={() => {
              this.setState({ currentStep: STEPS.step3, saveDataError: false });
            }}
          >
            Back
          </SecondaryButton>
          <Flex flex={1} column ml={2}>
            <Button
              onClick={() => {
                const data = {
                  other_income: this.state.otherIncome,
                  deductions: this.state.deductions,
                  extra_withholding: this.state.extraWithholding,
                };
                this.saveFormData(data, () => {
                  this.setState({
                    currentStep: STEPS.step5,
                  });
                });
              }}
              loading={this.state.isSubmitting}
            >
              Next
            </Button>
          </Flex>
        </Flex>
      </Flex>
    );
  }

  renderStep5() {
    const { digest_key: digestKey } = this.props.action.w4_form_answer;
    const pdfURL = getSecurePDFLink(digestKey);
    return (
      <Flex column>
        {this.renderFixedModuleHeader(
          'Form W-4: Federal tax withholding',
          formLink,
        )}
        <Text fontSize="22px" bold>
          Review
        </Text>

        <StyledStateTaxFormInfo />
        <Flex column mt={33}>
          {this.renderExemptionStatus()}
          <Text fontSize="18px" bold>
            Personal information
          </Text>
          {this.renderW4Link()}
          <Box mt={16} />
          {this.renderStep1Form(true)}

          {!this.state.isExemptFromWithholding && (
            <Box>
              {/* step2 result */}
              <Flex mt={44} column>
                <Text fontSize="18px" bold>
                  Multiple Jobs or Spouse Works
                </Text>
                <Box mt={16} />
                {this.renderStep2Form(true)}
              </Flex>

              {/* step3 result */}
              <Flex mt={44} column>
                <Text fontSize="18px" bold>
                  Claim Dependents
                </Text>
                <Box mt={16} />
                {this.renderStep3Form(true)}
              </Flex>

              {/* step3 result */}
              <Flex mt={44} column>
                <Text fontSize="18px" bold>
                  Other Adjustments
                </Text>
                <Box mt={16} />
                {this.renderStep4Form(true)}
              </Flex>
            </Box>
          )}
        </Flex>

        {this.props.viewResultAsManager
          && this.props.companyStaff.pii_permission === 'full_access' && (
            <Flex column>
              <Flex alignItems="center">
                <Text mr={16}>Download signed documents</Text>
                <a href={pdfURL} target="_blank" rel="noopener noreferrer">
                  <img src="/j/images/onboarding/download.svg" />
                </a>
              </Flex>
            </Flex>
        )}
        <Flex mt={50}>
          <SecondaryButton
            onClick={() => {
              if (this.state.isExemptFromWithholding) {
                this.setState({
                  currentStep: STEPS.step1,
                  saveDataError: false,
                });
              } else {
                this.setState({
                  currentStep: STEPS.step4,
                  saveDataError: false,
                });
              }
            }}
          >
            Back
          </SecondaryButton>
          <Flex flex={1} column ml={2}>
            <Button
              disabled={this.props.viewResultAsManager}
              onClick={() => {
                const { digest_key: digestKey } = this.props.action.w4_form_answer;
                const path = `${APIHOST}/v1/w4_form_answers/${digestKey}/sign`;
                this.setState({ isSubmitting: true, saveDataError: false });
                Axios.post(path)
                  .then((responseData) => {
                    this.setState({ isSubmitting: false });
                    const { sign_url: signLink } = responseData.data;
                    this.setState(
                      { signLink, currentStep: STEPS.step6 },
                      () => {
                        this.initHelloSign();
                      },
                    );
                  })
                  .catch((error) => {
                    let saveDataError = 'We were unable to process your request. Please try again.';
                    if (error && error.response && error.response.data) {
                      const { error_code: errorCode, message } = error.response.data;
                      saveDataError = message;
                    }
                    this.setState({
                      isSubmitting: false,
                      saveDataError: true,
                      saveDataError,
                    });
                  });
              }}
              loading={this.state.isSubmitting}
            >
              Finalize and Sign
            </Button>
          </Flex>
        </Flex>
        <Flex justifyContent="flex-end">
          {this.props.viewResultAsManager && (
            <ActionNote text="This step has already been completed." />
          )}
        </Flex>
      </Flex>
    );
  }

  renderStep6() {
    // const { signURL } = this.state;
    const { signError } = this.state;
    return (
      <Flex column>
        {this.renderFixedModuleHeader(
          'Form W-4: Federal tax withholding',
          formLink,
        )}
        <Text fontSize="22px" bold>
          Finalize and sign
        </Text>
        <Flex column mt={44}>
          <div id="iframe-container" style={{ height: '640px' }} />
        </Flex>
        {signError && <Text color="red">{signError}</Text>}
      </Flex>
    );
  }

  renderHeader() {
    const W4PDFLink = (
      <a href={formLink} target="_blank" rel="noreferrer">
        Federal Form W-4
      </a>
    );
    const { currentStep } = this.state;
    if (
      currentStep === STEPS.step0
      || currentStep === STEPS.exempt_status
      || currentStep === STEPS.step1
      || currentStep === STEPS.step5
      || currentStep === STEPS.step6
    ) { return null; }
    let noteComponent;
    if (currentStep === STEPS.step2) {
      noteComponent = (
        <>
          <Text bold color="#000">
            Complete Step 2-4 ONLY if they apply to you
          </Text>
          <Flex mt={1} color="#000">
            Refer to
            <Box mx={1}>{W4PDFLink}</Box>
            page 2
          </Flex>
        </>
      );
    } else if (currentStep === STEPS.step3 || currentStep === STEPS.step4) {
      noteComponent = (
        <>
          <Text bold color="#000">
            Complete Step 2-4 ONLY if they apply to you
          </Text>
          <Flex mt={1} color="#000">
            Refer to
            <Box mx={1}>{W4PDFLink}</Box>
            page 2
          </Flex>
          <Flex mt={23} column>
            <Text bold color="#000">
              Complete Steps 3–4(b) on Form W-4 for only ONE of these jobs.
            </Text>
            <Flex mt={1} color="#000">
              Leave those steps blank for the other jobs. (Your withholding will
              be most accurate if you complete Steps 3–4(b) on the Form W-4 for
              the highest paying job.)
            </Flex>
          </Flex>
        </>
      );
    }
    return ReactDom.createPortal(
      <Flex
        bg="#FFF7E8"
        mx={-30}
        mt={[-72, -55]}
        py={20}
        mb={48}
        minHeight={92}
      >
        <Flex column style={{ width: '310px', margin: 'auto' }}>
          {noteComponent}
        </Flex>
      </Flex>,
      this.el,
    );
  }

  render() {
    const { currentStep, saveDataError } = this.state;
    const isSigningStep = currentStep === STEPS.step6;

    return (
      <StateTaxFormLayout isSigningStep={isSigningStep} isOnboardingV2={this.isOnboardingV2}>
        {
          this.isOnboardingV2 ? (
            <PageTitle title="Form W-4" />
          ) : null
        }
        {currentStep === STEPS.step0 && this.renderStep0()}
        {currentStep === STEPS.exempt_status
          && this.renderExemptionStatusStep()}
        {currentStep === STEPS.step1 && this.renderStep1()}
        {currentStep === STEPS.step2 && this.renderStep2()}
        {currentStep === STEPS.step3 && this.renderStep3()}
        {currentStep === STEPS.step4 && this.renderStep4()}
        {currentStep === STEPS.step5 && this.renderStep5()}
        {currentStep === STEPS.step6 && this.renderStep6()}
        {saveDataError && (
          <Flex justifyContent="flex-end" mt={1}>
            <Text color="red">{saveDataError}</Text>
          </Flex>
        )}
      </StateTaxFormLayout>
    );
  }
}

export default EmployeeFillForm;
